import { Link } from 'react-router-dom';

import { ROUTES } from '@/config/routes';
import { getClubAnalyticsApi } from '@/lib/api/clubsApi';
import { queryKeys } from '@/lib/query/queryKeys';
import { useAuthStore } from '@/stores/auth-store';
import { getInitials } from '@shared';
import type { AuthUser } from '@shared';
import { useQuery } from '@tanstack/react-query';
import { ArrowRight, ChevronRight, ExternalLink, LayoutDashboard, Shield, Trophy, Users } from 'lucide-react';

const ADMIN_URL = (import.meta.env.VITE_URL_ADMIN as string | undefined) ?? 'http://localhost:5231';

export function ClubAdminQuickActions({ user }: { user: AuthUser }) {
  return (
    <div className='mb-6 grid grid-cols-1 gap-4 sm:grid-cols-3'>
      <a
        href={`${ADMIN_URL}/club/dashboard`}
        className='group flex items-center gap-4 rounded-2xl bg-white p-5 shadow-sm border border-slate-100 transition hover:border-emerald-200 hover:shadow-md'
      >
        <div className='flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600 transition group-hover:bg-emerald-100'>
          <LayoutDashboard className='h-6 w-6' />
        </div>
        <div className='flex-1 min-w-0'>
          <p className='font-bold text-slate-900'>Tableau de bord club</p>
          <p className='text-xs text-slate-500'>Gérer mon club</p>
        </div>
        <ExternalLink className='h-4 w-4 shrink-0 text-slate-300 transition group-hover:text-emerald-500' />
      </a>

      <a
        href={`${ADMIN_URL}/club/members`}
        className='group flex items-center gap-4 rounded-2xl bg-white p-5 shadow-sm border border-slate-100 transition hover:border-blue-200 hover:shadow-md'
      >
        <div className='flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-600 transition group-hover:bg-blue-100'>
          <Users className='h-6 w-6' />
        </div>
        <div className='flex-1 min-w-0'>
          <p className='font-bold text-slate-900'>Mes membres</p>
          <p className='text-xs text-slate-500'>Adhérents du club</p>
        </div>
        <ExternalLink className='h-4 w-4 shrink-0 text-slate-300 transition group-hover:text-blue-500' />
      </a>

      {user.club ? (
        <Link
          to={ROUTES.CLUB_PROFILE.replace(':id', String(user.club.id))}
          className='group flex items-center gap-4 rounded-2xl bg-white p-5 shadow-sm border border-slate-100 transition hover:border-indigo-200 hover:shadow-md'
        >
          <div className='flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600 transition group-hover:bg-indigo-100'>
            <Shield className='h-6 w-6' />
          </div>
          <div className='flex-1 min-w-0'>
            <p className='font-bold text-slate-900'>Page publique club</p>
            <p className='text-xs text-slate-500'>Voir le profil public</p>
          </div>
          <ArrowRight className='h-4 w-4 shrink-0 text-slate-300 transition group-hover:text-indigo-500 group-hover:translate-x-0.5' />
        </Link>
      ) : (
        <Link
          to={ROUTES.SEARCH_CONCOURS}
          className='group flex items-center gap-4 rounded-2xl bg-white p-5 shadow-sm border border-slate-100 transition hover:border-indigo-200 hover:shadow-md'
        >
          <div className='flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600 transition group-hover:bg-indigo-100'>
            <Trophy className='h-6 w-6' />
          </div>
          <div className='flex-1 min-w-0'>
            <p className='font-bold text-slate-900'>Explorer les concours</p>
            <p className='text-xs text-slate-500'>Voir les concours publiés</p>
          </div>
          <ArrowRight className='h-4 w-4 shrink-0 text-slate-300 transition group-hover:text-indigo-500 group-hover:translate-x-0.5' />
        </Link>
      )}
    </div>
  );
}

export function ClubAdminStats({ user }: { user: AuthUser }) {
  const isAuthenticated = useAuthStore(s => s.isAuthenticated);
  const clubId = user.clubId;

  const { data: analytics, isLoading } = useQuery({
    queryKey: queryKeys.clubs.analytics(clubId!),
    queryFn: () => getClubAnalyticsApi(clubId!),
    enabled: isAuthenticated && !!clubId,
  });

  if (!clubId) return null;

  if (isLoading) {
    return (
      <div className='mb-6 grid grid-cols-2 gap-4 sm:grid-cols-4'>
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className='h-24 animate-pulse rounded-2xl bg-slate-100' />
        ))}
      </div>
    );
  }

  if (!analytics) return null;

  const statCards = [
    { label: 'Membres', value: analytics.overview.totalMembers, color: 'text-blue-600' },
    { label: 'Nouveaux ce mois', value: analytics.overview.newMembersThisMonth, color: 'text-emerald-600' },
    { label: 'Victoires (année)', value: analytics.overview.winsThisYear, color: 'text-amber-600' },
    { label: 'Podiums (année)', value: analytics.overview.podiumsThisYear, color: 'text-indigo-600' },
  ];

  return (
    <div className='mb-6 grid grid-cols-2 gap-4 sm:grid-cols-4'>
      {statCards.map(card => (
        <div key={card.label} className='rounded-2xl bg-white border border-slate-100 shadow-sm p-5 text-center'>
          <p className={`text-2xl font-extrabold ${card.color}`}>{card.value}</p>
          <p className='mt-1 text-xs font-medium text-slate-500'>{card.label}</p>
        </div>
      ))}
    </div>
  );
}

export function ClubAdminClubCard({ user }: { user: AuthUser }) {
  if (!user.club) return null;

  return (
    <div className='rounded-2xl bg-white border border-slate-100 shadow-sm p-6'>
      <h2 className='mb-4 flex items-center gap-2 text-base font-bold text-slate-900'>
        <Shield className='h-4 w-4 text-emerald-500' />
        Mon club
      </h2>
      <Link
        to={ROUTES.CLUB_PROFILE.replace(':id', String(user.club.id))}
        className='group flex items-center gap-3 rounded-xl bg-slate-50 p-3 transition hover:bg-slate-100'
      >
        <div className='flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-linear-to-br from-emerald-500 to-teal-600 text-xs font-bold text-white'>
          {getInitials(user.club.name)}
        </div>
        <div className='flex-1 min-w-0'>
          <p className='truncate text-sm font-bold text-slate-900'>{user.club.name}</p>
          {user.club.city && <p className='text-xs text-slate-500'>{user.club.city}</p>}
        </div>
        <ChevronRight className='h-4 w-4 shrink-0 text-slate-400 transition group-hover:text-emerald-500' />
      </Link>
    </div>
  );
}
